import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { Role } from 'src/app/core/constants/role.enum';
import { UserStore } from 'src/app/core/stores/user.store';

@Injectable({
  providedIn: 'root',
})
export class ViewerRouteGuard implements CanActivateChild {
  private readonly router = inject(Router);
  private readonly userStore = inject(UserStore);

  private readonly restrictedPrefixes = [
    '/homepage',
    '/new-round',
    '/past-rounds',
    '/recognition',
    '/issue-view',
    '/management',
    '/internal-management',
    '/system-settings',
    '/report',
    '/templates/new-template',
    '/templates/edit-template',
  ];

  canActivateChild(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const knownRole = this.userStore.userRole() ?? this.userStore.user().role;
    if (knownRole) {
      return this.checkAccessByRole(knownRole, state.url);
    }

    if (!this.userStore.isAuthorized()) {
      return true;
    }

    return this.userStore.loadCurrentUser().pipe(
      map(user => this.checkAccessByRole(user.role, state.url)),
      catchError(() => of(this.router.parseUrl('/dashboard')))
    );
  }

  private checkAccessByRole(role: Role, rawUrl: string): boolean | UrlTree {
    if (role !== Role.VIEWER) return true;

    const url = this.normalizeUrl(rawUrl);
    const hasRestrictedAccess = this.restrictedPrefixes.some(prefix => url === prefix || url.startsWith(`${prefix}/`));

    return hasRestrictedAccess ? this.router.parseUrl('/dashboard') : true;
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
